import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Briefcase, Building2, Users } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { homeFor } from "@/lib/roles";

export default function Register() {
  const { register, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "job_seeker" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const me = await register(form);
      toast.success(t("auth.createAccount"));
      navigate(homeFor(me.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen wo-bg flex items-center justify-center px-5 py-12">
      <div className="absolute inset-0 wo-glow-radial pointer-events-none" />
      <div className="absolute top-4 right-4 z-10"><LanguageSwitcher /></div>
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size="text-3xl" /></div>
        <div className="liquid-glass rounded-2xl p-8">
          <h1 className="font-display font-bold text-2xl text-slate-100 mb-1">{t("auth.createAccount")}</h1>
          <p className="text-slate-400 text-sm mb-6">{t("auth.registerSub")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "job_seeker", label: t("auth.jobSeeker"), icon: Briefcase },
                { v: "company", label: "Company", icon: Building2 },
                { v: "agency", label: "Agency", icon: Users },
              ].map((r) => (
                <button key={r.v} type="button" onClick={() => set("role", r.v)} data-testid={`role-${r.v}`}
                  className={`flex flex-col items-center gap-1.5 justify-center rounded-lg border py-3 text-xs font-medium transition-colors ${form.role === r.v ? "border-teal-500/50 bg-teal-500/10 text-teal-300" : "border-white/10 text-slate-400 hover:border-white/20"}`}>
                  <r.icon size={17} /> {r.label}
                </button>
              ))}
            </div>
            {form.role === "agency" && (
              <p className="text-xs text-slate-500 -mt-1">Employment agencies hire on behalf of client companies. You'll get client management & submission tools.</p>
            )}
            <div>
              <Label className="text-slate-300 text-sm">{t("auth.fullName")}</Label>
              <Input data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} required
                className="mt-1.5 bg-slate-950/40 border-white/10 text-slate-100" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">{t("auth.email")}</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required
                className="mt-1.5 bg-slate-950/40 border-white/10 text-slate-100" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">{t("auth.password")}</Label>
              <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6}
                className="mt-1.5 bg-slate-950/40 border-white/10 text-slate-100" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
            <Button data-testid="auth-register-submit-button" type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 py-6">
              {loading ? "..." : t("auth.createBtn")}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            {t("auth.alreadyHave")} <Link to="/login" className="text-teal-400 hover:underline">{t("auth.loginBtn")}</Link>
          </p>
          <p className="mt-4 text-center text-xs text-slate-500 leading-relaxed">
            By creating an account you agree to our{" "}
            <Link to="/legal#terms" className="text-teal-400 hover:underline">Terms</Link> and{" "}
            <Link to="/legal#privacy" className="text-teal-400 hover:underline">Privacy Policy</Link>.
          </p>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-500" data-testid="register-legal-links">
          <Link to="/legal#terms" className="hover:text-teal-300 transition-colors">Terms</Link>
          <span className="text-slate-700">·</span>
          <Link to="/legal#privacy" className="hover:text-teal-300 transition-colors">Privacy</Link>
          <span className="text-slate-700">·</span>
          <Link to="/legal#gdpr" className="hover:text-teal-300 transition-colors">GDPR</Link>
        </div>
      </div>
    </div>
  );
}
